import React, { useState } from "react";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaDownload,
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import Advanyageico from "../images/Icons/advantage-icon.png";
import Downloadico from "../images/Icons/download-icon.png";
import RelatedDocuments from "./RelatedDocuments";

const ProductOverview = () => {
  const location = useLocation();
  const product = location.state?.product;

  const [imageLoaded, setImageLoaded] = useState(false);

  const [openSections, setOpenSections] = useState({
    Performance: false,
    ProductVideo: false,
    Finishes: false,
    AvailableSizes: false,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Early check for missing product
  if (!product) {
    return (
      <div className="p-8 text-center text-lg text-gray-700">
        No product data found.
      </div>
    );
  }

  const sections = [
    {
      title: "Performance",
      content: product.performance?.length
        ? product.performance.join("")
        : "No performance details available.",
    },
    {
      title: "Product Video",
      content: product.productVideo?.length
        ? product.productVideo
            .map((item, index) => {
              const isUrl = /^(https?:\/\/[^\s]+)$/.test(item.trim());
              return isUrl
                ? `<button onclick="window.open('${item}', '_blank')" class='mb-2 mr-2 bg-[#4b72a4] hover:bg-blue-700 text-white px-4 py-2 rounded'>▶ Play Video ${index + 1}</button>`
                : `<p class='text-gray-700 mb-2'>${item}</p>`;
            })
            .join(" ")
        : "No Product Video details available.",
    },
    {
      title: "Finishes",
      content: (() => {
        const finishes = product.finishes || [];
        const colorRegex = /^#?[a-zA-Z]+$/;

        const normalItems = finishes.filter((item) => !colorRegex.test(item));
        const colorItems = finishes.filter((item) => colorRegex.test(item));

        const normalContent = normalItems.length
          ? `<div class='mb-2 text-base text-gray-800'>${normalItems.join(", ")}</div>`
          : "";

        const colorBoxes = colorItems
          .map(
            (color) => `
              <div class="flex items-center mr-4 mb-2">
                <span class="w-4 h-4 rounded-full mr-2 border-2" style="border-color: ${color.toLowerCase()}; background-color: ${color.toLowerCase()}"></span>
                <span class="text-gray-800 text-lg">${color}</span>
              </div>
            `
          )
          .join("");

        return normalContent + (colorBoxes ? `<div class='flex flex-wrap'>${colorBoxes}</div>` : "");
      })(),
    },
    {
      title: "Available Sizes",
      content: product.availableSizes?.length
        ? product.availableSizes.join("<br/>")
        : "No Available Sizes details available.",
    },
  ];

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download file.");
    }
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row p-6 gap-6 py-4">
        {/* Left Section */}
        <div className="w-full lg:w-1/3 space-y-6">
          {/* Advantages */}
          <div className="p-4 bg-white rounded-lg shadow-md">
            <h3 className="flex items-center text-lg md:text-2xl font-bold mb-4">
              <img
                src={Advanyageico}
                alt="Advantage-icon"
                className="w-9 h-9 mr-2"
              />
              Advantages
            </h3>
            {product.advantages?.length > 0 ? (
              <ul className="space-y-2 text-[#1B365D] text-base md:text-lg">
                {product.advantages.map((advantage, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <FaCheckCircle className="text-green-500 mt-1 min-w-[1.25rem]" />
                    <span>{advantage}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">No advantages available.</p>
            )}
          </div>

          {/* Downloads */}
          <div className="p-4 bg-white rounded-lg shadow-md">
            <h3 className="flex text-lg md:text-2xl font-bold mb-4">
              <img src={Downloadico} alt="Download-icon" className="w-8 h-8 mr-2" />
              DOWNLOADS
            </h3>
            <div className="space-y-2">
              {product?.pdfNames?.map((name, index) => {
                const pdfUrl = product?.pdfUrls?.[index];
                if (!pdfUrl) return null;
                return (
                  <button
                    key={index}
                    onClick={() => handleDownload(pdfUrl, name + ".pdf")}
                    className="flex items-center w-full p-2 border border-gray-800 bg-white text-[#1B365D] rounded-md hover:!bg-[#1B365D] hover:!text-white transition"
                  >
                    <FaDownload className="mr-2 flex-shrink-0" />
                    <span className="truncate">{name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Expert Consultation */}
          <div className="p-4 bg-white rounded-lg shadow-md">
            <h2 className="text-xl md:text-2xl font-bold mb-2 text-black text-center">
              CONSULT OUR EXPERTS
            </h2>
            <p className="text-gray-800 text-base md:text-lg mb-4">
              Regardless of your industry, we'll deliver solutions and recommend
              the products you need to succeed.
            </p>
            <Link to="/contact-us">
              <button className="px-6 w-full py-2 border-2 border-[#0C2340] text-[#0C2340] hover:bg-[#1B365D] hover:text-white font-semibold rounded-lg transition duration-300">
                ASK OUR EXPERT
              </button>
            </Link>
          </div>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-2/3 bg-white p-4 rounded-lg space-y-8">
          {/* Product Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="text-[#0C2340]">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                {product.title?.[0] || "Product Overview"}
              </h2>
              <p
                className="text-base md:text-lg leading-relaxed text-justify"
                dangerouslySetInnerHTML={{
                  __html: product.descriptions?.[0] || "",
                }}
              />
            </div>

            <div className="relative w-full h-96 rounded flex items-center justify-center">
              {!imageLoaded && <div className="image-spinner"></div>}
              <img
                src={`${product.imageUrl}?t=${new Date().getTime()}`}
                alt={product?.titles || "Product"}
                className={`w-full h-96 object-contain rounded absolute top-0 left-0 transition-opacity duration-500 ${
                  imageLoaded ? "opacity-100" : "opacity-0"
                }`}
                onLoad={() => setImageLoaded(true)}
              />
            </div>

            <style>
              {`
               .image-spinner {
                 border: 4px solid #e5e7eb;
                 border-top: 4px solid #0c2341;
                 border-radius: 9999px;
                 width: 40px;
                 height: 40px;
                 animation: spin 0.8s linear infinite;
               }
               @keyframes spin {
                 0% { transform: rotate(0deg); }
                 100% { transform: rotate(360deg); }
               }
            `}
            </style>
          </div>

          {/* Product Composition */}
          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold">
              {product.title?.[1] || "Product Details"}
            </h2>
            <div
              className="text-base md:text-lg leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: product.descriptions?.[1] || "",
              }}
            />
            <div
              className="text-base md:text-lg leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: product.descriptions?.[2] || "",
              }}
            />
          </div>

          {/* Expandable Sections */}
          <div className="space-y-4">
            {sections.map((section) => (
              <div key={section.title} className="border border-gray-200 rounded-lg">
                <div
                  onClick={() =>
                    toggleSection(section.title.replace(/\s+/g, ""))
                  }
                  className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-[#0C2340]">
                    {section.title}
                  </h3>
                  {openSections[section.title.replace(/\s+/g, "")] ? (
                    <FaChevronUp className="text-gray-500" />
                  ) : (
                    <FaChevronDown className="text-gray-500" />
                  )}
                </div>
                {openSections[section.title.replace(/\s+/g, "")] && (
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div
                      className="text-base text-gray-700"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <RelatedDocuments />
    </>
  );
};

export default ProductOverview;
